package gr.tech.meetinggenie.styles

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.*

// ─── Shared Input Container ──────────────────────────────────────────────────

val InputContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .margin(bottom = 4.px)
    }
}

// ─── Text / TextArea ─────────────────────────────────────────────────────────

val GeneralInputLabelStyle = CssStyle {
    base {
        Modifier
            .fontWeight(FontWeight.Medium)
            .color(Theme.DarkGreen.rgb)
            .fontSize(0.875.cssRem)
            .margin(bottom = 4.px)
    }
}

val GeneralInputStyle = CssStyle {
    base {
        Modifier
            .height(32.px)
            .noBorder()
            .outline(1.px, LineStyle.Solid, Color("#D1D5DB"))
            .backgroundColor(Colors.White)
            .borderRadius(4.px)
            .fontSize(1.cssRem)
            .padding(left = 4.px)
            .color(Colors.Black)
            .styleModifier {
                property("transition", "outline-color 150ms ease, box-shadow 150ms ease")
            }
    }
    focus {
        Modifier
            .outline(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .styleModifier {
                property("box-shadow", "0 0 0 3px rgba(74, 158, 95, 0.18)")
                property("outline-offset", "0px")
            }
    }
}

val GeneralTextAreaStyle = CssStyle {
    base {
        Modifier
            .height(48.px)
            .fillMaxWidth()
            .noBorder()
            .backgroundColor(Colors.White)
            .outline(1.px, LineStyle.Solid, Colors.DarkGrey)
            .borderRadius(6.px)
            .fontSize(1.cssRem)
            .padding(8.px)
            .color(Colors.Black)
            .resize(Resize.Vertical)
    }
}

val GeneralFormErrorTextStyle = CssStyle {
    base {
        Modifier
            .height(20.px)
            .fontWeight(FontWeight.Medium)
            .fontSize(0.8.cssRem)
            .color(Colors.Red)
            .fontStyle(FontStyle.Italic)
            .margin(0.px)
    }
}

val GeneralInputErrorStyle = CssStyle {
    base {
        Modifier
            .outline(1.px, LineStyle.Solid, Colors.Red)
    }
}

// ─── Radio Group ─────────────────────────────────────────────────────────────

/** Vertical stack that holds a labelled radio group. */
val RadioGroupContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(6.px)
            .margin(bottom = 4.px)
    }
}

/** Label text sitting to the right of a custom radio circle. */
val RadioOptionLabelStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(8.px)
            .cursor(Cursor.Pointer)
            .fontSize(0.95.cssRem)
            .color(Colors.Black)
    }
    hover {
        Modifier
            .color(Theme.DarkGreen.rgb)
            .transition(Transition.all(100.ms))
    }
}

/**
 * Custom-styled radio `<input>`.
 * Uses `appearance: none` to replace the native button with a green circle.
 */
val RadioInputStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("appearance", "none")
                property("-webkit-appearance", "none")
            }
            .width(17.px)
            .height(17.px)
            .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .borderRadius(50.percent)
            .backgroundColor(Colors.White)
            .cursor(Cursor.Pointer)
            .flexShrink(0)
            .transition(Transition.all(120.ms))
    }
    cssRule(":checked") {
        Modifier
            .backgroundColor(Theme.DarkGreen.rgb)
            .border(2.px, LineStyle.Solid, Theme.DarkGreen.rgb)
    }
    cssRule(":hover:not(:checked)") {
        Modifier
            .border(2.px, LineStyle.Solid, Theme.DarkGreen.rgb)
    }
}

// ─── Select (table cell dropdown) ────────────────────────────────────────────

val TableSelectStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontSize(0.875.cssRem)
            .padding(4.px)
            .noBorder()
            .backgroundColor(Colors.White)
            .cursor(Cursor.Pointer)
    }
    focus {
        Modifier
            .transition(Transition.all(150.ms))
            .outline(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
    }
}

// ─── Checkbox ────────────────────────────────────────────────────────────────

/** Custom-styled checkbox — same green palette as radio, square with rounded corners. */
val CheckboxInputStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("appearance", "none")
                property("-webkit-appearance", "none")
            }
            .width(15.px)
            .height(15.px)
            .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .borderRadius(3.px)
            .backgroundColor(Colors.White)
            .cursor(Cursor.Pointer)
            .flexShrink(0)
            .transition(Transition.all(120.ms))
    }
    cssRule(":checked") {
        Modifier
            .backgroundColor(Theme.DarkGreen.rgb)
            .border(2.px, LineStyle.Solid, Theme.DarkGreen.rgb)
    }
    cssRule(":hover:not(:checked)") {
        Modifier.border(2.px, LineStyle.Solid, Theme.DarkGreen.rgb)
    }
}

/** Flex row label wrapping a checkbox + its text. */
val CheckboxOptionLabelStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(6.px)
            .cursor(Cursor.Pointer)
            .fontSize(0.8.cssRem)
    }
    hover {
        Modifier
            .color(Theme.DarkGreen.rgb)
            .transition(Transition.all(100.ms))
    }
}
